import { useActivePlannerId, useNavigateAsync, useViewModel } from '@/hooks';
import { Stack, Typography } from '@mui/material';
import { SxProps } from '@mui/system';
import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import LocalizedStrings from 'strings';
import { ColorIndicator } from '../../lists';
import { WorkloadInformationView } from '../../shared';
import { Dialog } from '../../utils';
import { PlannerWorkloadItemGroupsList } from './PlannerWorkloadItemGroupsList';
import { PlannerWorkloadPublishedWorkDetailsDialog } from './PlannerWorkloadPublishedWorkDetailsDialog';

export interface CourseSectionWorkloadInfoDialogProps {
  sx?: SxProps;
  className?: string;
  isOpen: boolean;
  courseSectionId: string;
  schoolId: string;
  date: Date;
}

export const CourseSectionWorkloadInfoDialog = observer(
  ({ sx = [], courseSectionId, className, isOpen, date, schoolId }: CourseSectionWorkloadInfoDialogProps) => {
    const plannerId = useActivePlannerId();

    const navigate = useNavigateAsync();
    const [searchParams] = useSearchParams();
    const workId = searchParams.get('workloadWorkId');

    function closeWorkDetails() {
      void navigate(-1);
    }

    function close() {
      return navigate(-1);
    }

    const viewModel = useViewModel(
      (viewModels) =>
        viewModels.createCourseSectionWorkloadInfoViewModel(plannerId, date, courseSectionId, schoolId, close),
      [plannerId, date, courseSectionId, schoolId]
    );

    const selectedItem = useMemo(() => {
      if (workId == null) {
        return undefined;
      }
      return viewModel.getPublishedWorkInfo(workId);
    }, [workId, viewModel]);

    return (
      <>
        <Dialog
          isOpen={isOpen}
          width="md"
          sx={sx}
          className={className}
          viewModel={viewModel}
          title={LocalizedStrings.planner.workload.courseSectionInfoDialogTitle()}
          contentPadding={{ top: 1, bottom: 1 }}
          renderData={() => (
            <Stack spacing={2} sx={{ width: '100%', position: 'relative' }}>
              <Stack spacing={1}>
                <Typography sx={{ fontWeight: 600 }}>{format(date, 'PPP')}</Typography>
                <Stack direction="row" spacing={1}>
                  <ColorIndicator color={viewModel.courseSection.courseSection?.color} />
                  <Stack sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{viewModel.courseSection.courseSection?.title}</Typography>

                    {viewModel.courseSection.courseSection?.section != null && (
                      <Typography variant="caption" color={'textSecondary'}>
                        {viewModel.courseSection.courseSection.section}
                      </Typography>
                    )}
                  </Stack>

                  <WorkloadInformationView
                    date={date}
                    getWorkloadInformation={() => viewModel.getCourseSectionWorkloadInformationForDate()}
                    alwaysShowStatus
                  />
                </Stack>
              </Stack>
              <PlannerWorkloadItemGroupsList getGroups={() => viewModel.getWorkloadGroups()} date={date} />
            </Stack>
          )}
        />

        {selectedItem != null && (
          <PlannerWorkloadPublishedWorkDetailsDialog
            isOpen
            onClose={closeWorkDetails}
            item={selectedItem}
            sx={{ zIndex: 100_001 }}
          />
        )}
      </>
    );
  }
);
